import cx from 'classnames';
import { ChevronDown, ChevronUp, DotsVertical } from 'ethena-icons';
import _ from 'lodash';
import {
  Children,
  cloneElement,
  createContext,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { Overlay } from 'react-bootstrap';
import { Placement } from 'react-bootstrap/esm/Overlay';
import { useOnClickOutside } from '../../../lib/hooks/useOnClickOutside';
import css from './KebabMenu.module.scss';

const MODAL_Z_INDEX = 1050;

const KebabMenuContext = createContext({
  isOpen: false,
  setIsMenuOpen: _.noop,
  setToggleNode: _.noop,
  toggleNode: null,
});

const useKebabMenuContext = () => useContext(KebabMenuContext);

interface IKebabMenuProps {
  children: ReactNode;
  onOpen?: (isOpen: boolean) => void;
}

const KebabMenu = ({ children, onOpen }: IKebabMenuProps) => {
  const [isOpen, setIsMenuOpen] = useState(false);
  const [toggleNode, setToggleNode] = useState(null);

  const handleSetMenuOpen = (isOpen: boolean) => {
    setIsMenuOpen(isOpen);
    onOpen?.(isOpen);
  };

  return (
    <KebabMenuContext.Provider
      value={{
        isOpen,
        setIsMenuOpen: handleSetMenuOpen,
        setToggleNode,
        toggleNode,
      }}
    >
      {children}
    </KebabMenuContext.Provider>
  );
};

interface IOptionsProps {
  children: ReactNode;
  className?: string;
  placement?: Placement;
  onClose?: () => void;
}

const Options = ({
  children,
  className,
  placement = 'bottom-end',
  onClose,
  ...rest
}: IOptionsProps) => {
  const { isOpen, setIsMenuOpen, toggleNode } = useKebabMenuContext();
  const optionsRef = useRef();

  useOnClickOutside(optionsRef, (event: MouseEvent<HTMLElement>) => {
    if (!toggleNode.contains(event.target) && isOpen) {
      setIsMenuOpen(false);
      onClose?.();
    }
  });

  return (
    <Overlay
      flip
      onHide={() => setIsMenuOpen(false)}
      placement={placement}
      rootClose={true}
      show={isOpen}
      target={toggleNode}
      {...rest}
    >
      {({ arrowProps, placement, popper, show, style, ...restMenuProps }) => (
        <menu
          {...restMenuProps}
          className={className}
          style={{
            ...style,
            zIndex: MODAL_Z_INDEX + 1,
            minWidth: toggleNode.offsetWidth,
          }}
        >
          <ul className={css.options} ref={optionsRef}>
            {children}
          </ul>
        </menu>
      )}
    </Overlay>
  );
};

export interface IOptionProps {
  children?:
    | ReactNode
    | ((props: { setIsMenuOpen: (isOpen: boolean) => void }) => ReactNode);
  className?: string;
  closeMenuOnClick?: boolean;
  isDisabled?: boolean;
  onClick?: (event: MouseEvent<HTMLLIElement>) => void;
  onKeyPress?: (event: KeyboardEvent<HTMLLIElement>) => void;
  stopPropagation?: boolean;
}

const Option = ({
  children = undefined,
  className = undefined,
  closeMenuOnClick = true,
  isDisabled = false,
  onClick = _.noop,
  onKeyPress = _.noop,
  stopPropagation = true,
  ...rest
}: IOptionProps) => {
  const { setIsMenuOpen } = useKebabMenuContext();

  const handleClick = (event: MouseEvent<HTMLLIElement>) => {
    if (isDisabled) {
      event.stopPropagation();
      return;
    }
    // This is to prevent a click from propagating the event to a parent node.
    if (stopPropagation) {
      event.stopPropagation();
    }
    // If for some reason you didn't want to close the menu after an option was
    // clicked, you could pass in this prop with `false`.
    if (closeMenuOnClick) {
      setIsMenuOpen(false);
    }
    onClick(event);
  };

  return (
    <li
      className={cx(css.option, className, {
        [css.disabled]: isDisabled,
      })}
      onClick={handleClick}
      {...rest}
    >
      {_.isFunction(children) ? children({ setIsMenuOpen }) : children}
    </li>
  );
};

interface IToggleProps {
  children?: ReactNode;
  className?: string;
  includeChevron?: boolean;
  isDisabled?: boolean;
  openClassName?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}

const Toggle = ({
  children = undefined,
  className,
  includeChevron = false,
  isDisabled = false,
  openClassName,
  onClick,
}: IToggleProps) => {
  const { isOpen, setIsMenuOpen, setToggleNode } = useKebabMenuContext();
  const toggleRef = useCallback(
    (node: HTMLButtonElement | null) => setToggleNode(node),
    []
  );

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsMenuOpen(!isOpen);
    onClick?.(event);
  };

  if (children) {
    return includeChevron ? (
      <button
        className={cx(css.customToggle, className)}
        disabled={isDisabled}
        onClick={handleClick}
        ref={toggleRef}
      >
        {children}
        {isOpen ? <ChevronUp /> : <ChevronDown />}
      </button>
    ) : (
      <>
        {Children.map(children, (child: ReactElement) =>
          cloneElement(child, {
            disabled: isDisabled,
            onClick: handleClick,
            ref: toggleRef,
          })
        )}
      </>
    );
  } else {
    return (
      <button
        className={cx(css.toggle, className, {
          [css.open]: isOpen,
          [openClassName]: isOpen,
        })}
        disabled={isDisabled}
        ref={toggleRef}
        onClick={handleClick}
      >
        <DotsVertical height={22} width={22} />
      </button>
    );
  }
};

KebabMenu.Options = Options;
KebabMenu.Option = Option;
KebabMenu.Toggle = Toggle;

export { KebabMenu };
